const PESO = "₱"; // ₱

function safe(v, fallback = "") {
  return v === undefined || v === null ? fallback : v;
}
function fmtNum(n) {
  return Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtMoney(n) {
  return `${PESO}${fmtNum(n)}`;
}
function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

// Stacked, centered business letterhead — matches a printed sales/purchase receipt.
function ReceiptHeader({ rh }) {
  const lines = [
    rh.registration_number ? `Reg No: ${rh.registration_number}` : "",
  ].filter(Boolean);
  const legacyMeta = [
    rh.contact_number ? `Tel: ${rh.contact_number}` : "",
    rh.tin ? `TIN: ${rh.tin}` : "",
    rh.vat_registration || "",
  ].filter(Boolean).join("  ·  ");

  return (
    <div className="text-center" data-testid="receipt-business-header">
      {rh.logo_url ? (
        <img src={rh.logo_url} alt="" style={{ maxHeight: "16mm", margin: "0 auto 1.5mm", objectFit: "contain" }} />
      ) : null}
      <div style={{ fontFamily: "Work Sans, Arial, sans-serif", fontWeight: 800, fontSize: "1.15em", lineHeight: 1.15, whiteSpace: "pre-line" }}>
        {safe(rh.business_name) || "BUSINESS NAME"}
      </div>
      {rh.registered_name ? (
        <div style={{ fontWeight: 600, fontSize: "0.92em", marginTop: "0.6mm", lineHeight: 1.2 }}>{rh.registered_name}</div>
      ) : null}
      {lines.map((l, i) => (
        <div key={i} style={{ fontSize: "0.86em", marginTop: "0.4mm" }}>{l}</div>
      ))}
      {rh.address ? (
        <div style={{ fontSize: "0.82em", marginTop: "0.6mm", fontStyle: "italic", lineHeight: 1.2, whiteSpace: "pre-line" }}>{rh.address}</div>
      ) : null}
      {rh.email ? <div style={{ fontSize: "0.82em", marginTop: "0.4mm" }}>Email: {rh.email}</div> : null}
      {rh.phone_mobile ? <div style={{ fontSize: "0.82em" }}>Phone: {rh.phone_mobile} (Mobile)</div> : null}
      {rh.phone_landline ? <div style={{ fontSize: "0.82em" }}>Phone: {rh.phone_landline} (Fixed Line)</div> : null}
      {legacyMeta ? <div style={{ fontSize: "0.78em", marginTop: "0.4mm", color: "#475569" }}>{legacyMeta}</div> : null}
      <div style={{ borderBottom: "1.5px solid #0f172a", marginTop: "1.5mm" }} />
    </div>
  );
}

export default function POSReceiptPreview({ transaction, settings = {}, receiptHeader = null }) {
  if (!transaction) return null;
  // Business receipt letterhead only — a receipt NEVER shows the doctor's Rx
  // letterhead. Prefer the snapshot captured on the transaction; if it has no
  // business name (older/blank snapshots) use the live hospital-derived header.
  const snap = transaction.receipt_header_snapshot || {};
  const live = receiptHeader || {};
  const rh = (snap.business_name || snap.logo_url || snap.address) ? snap : { ...snap, ...live };
  const patient = transaction.patient_snapshot || {};
  const totals = transaction.totals || {};
  const lineItems = transaction.line_items || [];
  const payments = transaction.payments || [];

  const title = safe(rh.receipt_title).trim() || "Purchase Order";
  const paper = rh.paper_width || "80mm";
  const isThermal = paper === "58mm" || paper === "80mm";
  // Fill the full roll width — a fixed mm width left unused margins on the paper.
  const contentW = isThermal ? "100%" : "100%";
  const contentMaxW = paper === "58mm" ? "58mm" : paper === "A4" ? "190mm" : "80mm";
  const baseFont = paper === "58mm" ? "8pt" : paper === "A4" ? "10.5pt" : "9.5pt";
  const pageCss = isThermal
    ? `@page { size: ${paper} auto; margin: 0; }`
    : `@page { size: A4; margin: 10mm; }`;

  const invoiceDate = fmtDate(transaction.paid_at || transaction.created_at);
  const customer =
    safe(patient.full_name) +
    (patient.patient_number ? ` (${patient.patient_number})` : "");
  const grand = totals.grand_total ?? transaction.grand_total;

  const cellLabel = { border: "1px solid #0f172a", padding: "0.8mm 1.5mm", fontWeight: 600, width: "38%", verticalAlign: "middle" };
  const cellVal = { border: "1px solid #0f172a", padding: "0.8mm 1.5mm", verticalAlign: "middle" };

  return (
    <div className="print-area" data-testid="pos-receipt-preview">
      <style>{`@media print { ${pageCss} .no-print { display:none !important; } }`}</style>
      <div
        data-testid="pos-receipt-page"
        style={{
          width: contentW,
          maxWidth: contentMaxW,
          margin: "0 auto",
          background: "#fff",
          color: "#0f172a",
          fontFamily: "Arial, sans-serif",
          fontSize: baseFont,
          lineHeight: 1.35,
          padding: isThermal ? "1.5mm" : "6mm",
          boxSizing: "border-box",
        }}
      >
        <ReceiptHeader rh={rh} />

        {/* Document title */}
        <div className="text-center" style={{ margin: "3mm 0 2.5mm" }}>
          <span
            style={{ display: "inline-block", border: "2px solid #0f172a", padding: "0.8mm 4mm", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.12em", fontSize: "1.05em", fontFamily: "Work Sans, Arial, sans-serif" }}
            data-testid="receipt-title"
          >
            {title}
          </span>
        </div>

        {/* Invoice block */}
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "3mm" }} data-testid="receipt-invoice-block">
          <tbody>
            <tr>
              <td style={cellLabel}>Invoice To</td>
              <td style={cellVal} data-testid="receipt-customer">{customer || "—"}</td>
            </tr>
            <tr>
              <td style={cellLabel}>Invoice ID</td>
              <td style={cellVal} data-testid="receipt-invoice-number">{safe(transaction.transaction_number, "DRAFT")}</td>
            </tr>
            <tr>
              <td style={cellLabel}>Invoice Date</td>
              <td style={cellVal}>{invoiceDate}</td>
            </tr>
            <tr>
              <td style={cellLabel}>Due Date</td>
              <td style={cellVal}>{invoiceDate}</td>
            </tr>
            <tr>
              <td style={cellLabel}>Amount Due</td>
              <td style={{ ...cellVal, fontWeight: 800, fontSize: "1.1em" }} data-testid="receipt-amount-due">{fmtMoney(grand)}</td>
            </tr>
          </tbody>
        </table>

        {/* Items */}
        <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="receipt-items-table">
          <thead>
            <tr style={{ borderTop: "1.5px solid #0f172a", borderBottom: "1.5px solid #0f172a" }}>
              <th style={{ textAlign: "left", padding: "0.8mm 0", fontWeight: 700 }}>Item</th>
              <th style={{ textAlign: "right", padding: "0.8mm 0", fontWeight: 700 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.length === 0 ? (
              <tr><td colSpan={2} style={{ textAlign: "center", color: "#94a3b8", fontStyle: "italic", padding: "3mm 0" }}>No items.</td></tr>
            ) : (
              lineItems.map((li, i) => (
                <tr key={li.id || i} style={{ borderBottom: "0.4px dashed #cbd5e1" }} data-testid={`receipt-row-${i}`}>
                  <td style={{ padding: "0.8mm 0", verticalAlign: "top" }}>
                    <span style={{ fontWeight: 600 }}>{li.name}</span>
                    <span style={{ color: "#475569" }}>, {li.quantity}x{fmtNum(li.unit_price)}</span>
                    {li.unit ? <span style={{ color: "#94a3b8" }}> /{li.unit}</span> : null}
                  </td>
                  <td style={{ padding: "0.8mm 0", textAlign: "right", verticalAlign: "top", whiteSpace: "nowrap" }}>{fmtNum(li.line_total)}</td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            {(totals.transaction_discount_total || 0) > 0 || (totals.line_discount_total || 0) > 0 ? (
              <tr>
                <td style={{ textAlign: "right", padding: "1mm 0 0", color: "#b91c1c" }}>Discount</td>
                <td style={{ textAlign: "right", padding: "1mm 0 0", color: "#b91c1c", whiteSpace: "nowrap" }}>
                  -{fmtNum((totals.transaction_discount_total || 0) + (totals.line_discount_total || 0))}
                </td>
              </tr>
            ) : null}
            <tr style={{ borderTop: "1.5px solid #0f172a" }}>
              <td style={{ textAlign: "right", padding: "1mm 0 0" }}>Subtotal</td>
              <td style={{ textAlign: "right", padding: "1mm 0 0", whiteSpace: "nowrap" }} data-testid="receipt-subtotal">{fmtNum(totals.subtotal ?? grand)}</td>
            </tr>
            {(totals.tax_amount || 0) > 0 ? (
              <tr>
                <td style={{ textAlign: "right", padding: "0.4mm 0" }}>Tax</td>
                <td style={{ textAlign: "right", padding: "0.4mm 0", whiteSpace: "nowrap" }}>{fmtNum(totals.tax_amount)}</td>
              </tr>
            ) : null}
            <tr style={{ borderTop: "1.5px solid #0f172a" }}>
              <td style={{ textAlign: "right", padding: "1mm 0 0", fontWeight: 800, fontSize: "1.1em" }}>Total</td>
              <td style={{ textAlign: "right", padding: "1mm 0 0", fontWeight: 800, fontSize: "1.1em", whiteSpace: "nowrap" }} data-testid="receipt-grand-total">{fmtMoney(grand)}</td>
            </tr>
          </tfoot>
        </table>

        {/* A pro bono bill has no payment lines, so say plainly why. */}
        {transaction.pro_bono ? (
          <div
            style={{ marginTop: "2.5mm", borderTop: "0.4px dashed #94a3b8", paddingTop: "1.5mm", textAlign: "center" }}
            data-testid="receipt-pro-bono"
          >
            <div style={{ fontWeight: 700, letterSpacing: "0.1em" }}>PRO BONO</div>
            <div style={{ color: "#475569" }}>No payment collected</div>
            {transaction.pro_bono_reason ? (
              <div style={{ color: "#475569" }}>{transaction.pro_bono_reason}</div>
            ) : null}
          </div>
        ) : null}

        {/* Payments (kept compact; not shown on the sample PO but useful on a paid receipt) */}
        {payments.length > 0 ? (
          <div style={{ marginTop: "2.5mm", borderTop: "0.4px dashed #94a3b8", paddingTop: "1.5mm" }} data-testid="receipt-payments">
            {payments.map((p) => (
              <div key={p.id} style={{ display: "flex", justifyContent: "space-between" }}>
                <span>{p.method}{p.reference ? ` · ref ${p.reference}` : ""}</span>
                <span>{fmtNum(p.amount)}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#475569" }}>Amount Tendered</span><span>{fmtNum(transaction.amount_paid)}</span>
            </div>
            {(transaction.change_due || 0) > 0 ? (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#475569" }}>Change</span><span>{fmtNum(transaction.change_due)}</span>
              </div>
            ) : null}
          </div>
        ) : null}

        <div style={{ marginTop: "2.5mm", fontSize: "0.82em", color: "#475569" }}>
          {transaction.cashier_name ? <div>Cashier: {transaction.cashier_name}</div> : null}
          {transaction.queue_number ? <div>Queue #: {transaction.queue_number}</div> : null}
          {transaction.notes ? <div style={{ fontStyle: "italic" }}>Notes: {transaction.notes}</div> : null}
        </div>

        <div className="text-center" style={{ marginTop: "3mm", fontSize: "0.8em", color: "#64748b", whiteSpace: "pre-line" }}>
          {safe(rh.footer_note) || "Thank you for your purchase."}
        </div>
        <div className="text-center" style={{ marginTop: "1mm", fontSize: "0.72em", color: "#94a3b8" }}>
          {safe(transaction.transaction_number, "DRAFT")}
        </div>
      </div>
    </div>
  );
}
